import {
  Constructor,
  IEdge,
  type ILabel,
  type INode,
  type IRenderContext,
  LabelStyleBase,
  Rect,
  Size,
  Visual,
} from '@ardoq/yfiles';
import {
  type GraphNode,
  type CollapsibleGraphGroup,
  type GraphItem,
  type LeafNodeLayout,
  LabelExportSupport,
} from '@ardoq/graph';
import {
  measureNodeLabel,
  MODERNIZED_BLOCK_DIAGRAM_LINE_HEIGHT as LINE_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_FONT_SIZE,
} from '@ardoq/graph-labeling';
import { LabelStyleProperties, createMultiLabelExporter } from '@ardoq/export';
import { wrapString } from 'tabview/blocks/misc/string';
import {
  INVISIBLE_SEPARATOR,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_LABEL_WIDTH as LABEL_WIDTH,
  MODERNIZED_BLOCK_DIAGRAM_OTHER_LABEL_WIDTH as OTHER_LABEL_WIDTH,
} from './consts';
import {
  MODERNIZED_BLOCK_DIAGRAM_LABEL_MAX_LINES as MAX_LINES,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_PADDING as PADDING,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_SHAPE_HEIGHT as SHAPE_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_NODE_HEIGHT as NODE_HEIGHT,
} from 'yfilesExtensions/styles/modernized/consts';
import { wrapLeafNodeLabel } from './graphBuilder/wrapLeafNodeLabel';
import { CanvasOtherLabelVisual } from '../labels/CanvasOtherLabelVisual';
import { createLabelStyle, Preset } from '../shared/styleUtils';
import { collapsedComponentGroupShapeY } from 'yfilesExtensions/styles/modernized/leafNode/collapsedComponentGroupShapeY';
import {
  calculateOtherLabelsLayout,
  LEAF_NODE_OTHER_LABELS_CONFIG,
  otherLabelsTotalHeight,
  wrapTextArray,
  getLeafNodeLayout,
} from '../shared/layoutUtils';

const collapsedComponentGroupLayout = (label: ILabel): LeafNodeLayout => {
  const node = label.owner as INode;
  const {
    tag,
    layout: { height: nodeHeight },
  } = node;
  const mainLabel =
    (tag as CollapsibleGraphGroup).getItemLabels()?.mainLabel ?? '';
  const shapeY = collapsedComponentGroupShapeY(node);
  const wrappedMainLabel = wrapLeafNodeLabel(mainLabel);
  return { nodeHeight, shapeY, wrappedMainLabel };
};

const getWrappedOtherLabels = (label: ILabel): string[][] => {
  const otherLabels = label.text.split(INVISIBLE_SEPARATOR);
  return wrapTextArray(
    otherLabels,
    OTHER_LABEL_WIDTH,
    measureNodeLabel,
    MAX_LINES
  );
};

class LeafNodeOtherLabelStyle extends LabelStyleBase {
  public static getStyle(
    _context: IRenderContext,
    label: ILabel
  ): LabelStyleProperties[] {
    const isTransparentized = ((label.owner as INode | IEdge).tag as GraphItem)
      ?.isTransparentized;

    const wrappedOtherLabels = getWrappedOtherLabels(label);

    return wrappedOtherLabels
      .map(wrapped =>
        createLabelStyle(
          null,
          { kind: 'lines', lines: wrapped },
          { preset: Preset.Other, isTransparentized }
        )
      )
      .reverse();
  }

  public static getLayout(context: IRenderContext, label: ILabel): Rect[] {
    const node = label.owner as INode | null;
    if (!node) return [];

    const leafNodeLayout = getLeafNodeLayout(context);
    if (!leafNodeLayout) return [];

    const nodeId = (node.tag as GraphNode)?.id;
    const layout = nodeId ? leafNodeLayout.get(nodeId) : undefined;
    const { shapeY, wrappedMainLabel } =
      layout ?? collapsedComponentGroupLayout(label);
    const shapeBottom = shapeY + SHAPE_HEIGHT;
    const mainLabelHeight = wrappedMainLabel.length * LINE_HEIGHT;
    const mainLabelBottom = mainLabelHeight
      ? shapeBottom + PADDING + mainLabelHeight
      : shapeBottom;

    const wrapped = wrapTextArray(
      label.text.split(INVISIBLE_SEPARATOR),
      OTHER_LABEL_WIDTH,
      measureNodeLabel,
      MAX_LINES
    );

    return calculateOtherLabelsLayout(wrapped, {
      ...LEAF_NODE_OTHER_LABELS_CONFIG,
      startY: mainLabelBottom + PADDING,
    });
  }

  constructor() {
    super();
  }

  protected override lookup(label: ILabel, type: Constructor): any {
    if (type === LabelExportSupport) {
      const toAbsolute = (ctx: IRenderContext, lbl: ILabel): Rect[] => {
        const node = lbl.owner as INode | null;
        if (!node) return [];

        const relRects = LeafNodeOtherLabelStyle.getLayout(ctx, lbl);
        const baseX = node.layout.x;
        const baseY = node.layout.y;

        return relRects
          .map(
            r => new Rect(baseX + r.x + PADDING, baseY + r.y, r.width, r.height)
          )
          .reverse();
      };

      return {
        toJSON: createMultiLabelExporter(
          toAbsolute,
          LeafNodeOtherLabelStyle.getStyle
        ),
      } as LabelExportSupport;
    }
    return super.lookup(label, type);
  }

  override createVisual(context: IRenderContext, label: ILabel): Visual | null {
    const node = label.owner as INode;
    const { id, isTransparentized } = node.tag as GraphNode;
    const otherLabels = (node.tag as GraphNode).getItemLabels()?.otherLabels;
    if (!otherLabels?.length) return null;
    const styles = otherLabels.map(l => l[3]);

    const leafNodeLayout = getLeafNodeLayout(context);
    if (!leafNodeLayout) return null;

    const { shapeY, wrappedMainLabel } =
      leafNodeLayout.get(id) ?? collapsedComponentGroupLayout(label);

    const shapeBottom = shapeY + SHAPE_HEIGHT;
    const mainLabelHeight = wrappedMainLabel.length * LINE_HEIGHT;
    const mainLabelBottom = mainLabelHeight
      ? shapeBottom + PADDING + mainLabelHeight
      : shapeBottom;

    const wrapped = label.text
      .split(INVISIBLE_SEPARATOR)
      .map(t => wrapString(t, OTHER_LABEL_WIDTH, measureNodeLabel, MAX_LINES));

    const rects = calculateOtherLabelsLayout(wrapped, {
      ...LEAF_NODE_OTHER_LABELS_CONFIG,
    });

    const {
      layout: { bounds },
    } = label;
    const container = new Rect(
      bounds.x,
      bounds.y + mainLabelBottom + PADDING,
      bounds.width,
      otherLabelsTotalHeight(rects)
    );

    return new CanvasOtherLabelVisual(
      container,
      rects,
      wrapped,
      MODERNIZED_BLOCK_DIAGRAM_FONT_SIZE,
      LINE_HEIGHT,
      isTransparentized ? 0.32 : 1,
      styles
    );
  }

  override getPreferredSize(_label: ILabel): Size {
    // this isn't really the size of the label. it's not crucial we know that at this time.
    // this will determine whether the label is painted or not, if the bounds based on the layoutParameter and this size don't intersect with the viewport, it will be skipped.
    return new Size(LABEL_WIDTH, NODE_HEIGHT);
  }
}
export { LeafNodeOtherLabelStyle };
